import { useCallback, useRef } from 'react';
import type {
  SearchRequest,
  SearchDocumentsResult,
} from '@/types';
import { getAuthToken } from '@/api/client';
import { env } from '@/env';

export interface StreamSearchState {
  results: SearchDocumentsResult | undefined;
  answer: string;
  isLoading: boolean;
  isStreaming: boolean;
}

/**
 * Parse SSE lines from a text buffer. Returns parsed events and any remaining
 * incomplete data that should be carried over to the next chunk.
 */
function parseSSEEvents(buffer: string): {
  events: Array<{ event: string; data: string }>;
  remainder: string;
} {
  const events: Array<{ event: string; data: string }> = [];
  // Split on double newlines to find complete event blocks
  const parts = buffer.split('\n\n');
  // Last part may be incomplete
  const remainder = parts.pop() ?? '';

  for (const part of parts) {
    if (!part.trim()) continue;
    let event = 'message';
    let data = '';
    for (const line of part.split('\n')) {
      if (line.startsWith('event: ')) {
        event = line.slice(7);
      } else if (line.startsWith('data: ')) {
        data = line.slice(6);
      } else if (line === 'data:') {
        data = '';
      }
    }
    events.push({ event, data });
  }

  return { events, remainder };
}

export function useStreamSearch() {
  const abortRef = useRef<AbortController | null>(null);

  const streamSearch = useCallback(
    async (
      request: SearchRequest,
      callbacks: {
        onResults: (data: SearchDocumentsResult) => void;
        onToken: (token: string) => void;
        onDone: () => void;
        onError: (err: unknown) => void;
      },
    ) => {
      // Abort any in-flight request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const accessToken = await getAuthToken();
        const res = await fetch(`${env.api.baseUrl}/search/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify(request),
          signal: controller.signal,
        });

        if (!res.ok) {
          throw new Error(`Search failed: ${res.status}`);
        }

        const reader = res.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const { events, remainder } = parseSSEEvents(buffer);
          buffer = remainder;

          for (const evt of events) {
            if (evt.event === 'results') {
              try {
                const data = JSON.parse(evt.data) as SearchDocumentsResult;
                callbacks.onResults(data);
              } catch {
                console.error('Failed to parse results event');
              }
            } else if (evt.event === 'token') {
              callbacks.onToken(evt.data);
            } else if (evt.event === 'done') {
              callbacks.onDone();
            }
          }
        }

        // Process any remaining buffer
        if (buffer.trim()) {
          const { events } = parseSSEEvents(buffer + '\n\n');
          for (const evt of events) {
            if (evt.event === 'done') {
              callbacks.onDone();
            } else if (evt.event === 'token') {
              callbacks.onToken(evt.data);
            }
          }
        }

        callbacks.onDone();
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          callbacks.onError(err);
        }
      }
    },
    [],
  );

  const abort = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { streamSearch, abort };
}
